import { useCallback, useEffect, useMemo } from "react";

import {
  ActivityFilterType,
  buildStatusOptions,
  filtersToClearForResolvedEvents,
  hasFiltersThatNeedActiveEvents,
  labelByActivityFilterType,
  requestParamsByActivityFilterType,
} from "~/feature/dashboard/components/sortAndFilters/StatusFilterDropdown/config";
import { useFilter } from "~/providers/FilterProvider";
import { MetricsScopeEnum } from "~/types/metrics";
import { getActivityFilterValue } from "~/utils/filters/getActivityFilterValue";

export const useStatusFilterDropdown = () => {
  const { filters, updateFilters } = useFilter();

  const selectedStatus = useMemo(
    () =>
      getActivityFilterValue({
        active: filters.active,
        resolved: filters.resolved,
      }) ?? ActivityFilterType.All,
    [filters.active, filters.resolved]
  );

  const selectedScope = filters.metricsScope ?? MetricsScopeEnum.Event;
  const chipLabel = labelByActivityFilterType[selectedStatus];

  const isResolvedSelected = selectedStatus === ActivityFilterType.Resolved;
  const needsActiveEvents = hasFiltersThatNeedActiveEvents(filters);
  const isResolvedDisabled = !isResolvedSelected && needsActiveEvents;

  const statusOptions = useMemo(
    () =>
      buildStatusOptions(
        isResolvedDisabled ? ActivityFilterType.Resolved : null
      ),
    [isResolvedDisabled]
  );

  useEffect(() => {
    if (isResolvedSelected && needsActiveEvents) {
      updateFilters(filtersToClearForResolvedEvents);
    }
  }, [isResolvedSelected, needsActiveEvents, updateFilters]);

  const onChangeStatus = useCallback(
    (status: ActivityFilterType) => {
      updateFilters(requestParamsByActivityFilterType[status]);
    },
    [updateFilters]
  );

  const onChangeScope = useCallback(
    (metricsScope: MetricsScopeEnum) => {
      updateFilters({ metricsScope });
    },
    [updateFilters]
  );

  return {
    chipLabel,
    selectedStatus,
    selectedScope,
    statusOptions,
    onChangeStatus,
    onChangeScope,
  };
};
